import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';
import { MatSort, MatSortModule, Sort } from '@angular/material/sort';
import { CommonModule } from '@angular/common';
import { BuildingWithUsersDTO } from '@app/shared/models/buildingWithUsersDTO';
import { User } from '@app/shared/models/user.interface';
import { TimeUtils } from '@app/shared/utils/time.utils';
import { MatMiniFabButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { ManagingCompanyAuthorityType } from '@app/shared/models/managing-company-authorities.interface';

type UserWithBuilding = User & { buildingId: number; buildingName: string };

@Component({
  selector: 'app-managing-company-users-table',
  standalone: true,
  imports: [CommonModule, MatPaginatorModule, MatSortModule, MatTableModule, MatIcon, MatMiniFabButton],
  templateUrl: './managing-company-users-table.component.html',
  styleUrl: './managing-company-users-table.component.scss'
})
export class ManagingCompanyUsersTableComponent implements AfterViewInit, OnChanges {
  @Input() buildingUsers: BuildingWithUsersDTO[] = [];
  @Input() managingCompanyId: number | null = null;
  @Output() deleteRequested = new EventEmitter<{ buildingId: number; userId: number }>();
  @Output() editRequested = new EventEmitter<UserWithBuilding>();
  dataSource = new MatTableDataSource<UserWithBuilding>();

  displayedColumns = [
    'buildingName',
    'userID',
    'name',
    'emailAddress',
    'lastLoggedIn',
    'authMethod',
    'authSource',
    'actions'
  ];

  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;

  private didInit = false;

  constructor(private readonly changeDetectorRef: ChangeDetectorRef) {}

  ngAfterViewInit(): void {
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;

    this.sort.disableClear = true;

    this.dataSource.sortingDataAccessor = (usersBuildingsRow: UserWithBuilding, tableColumn: string) => {
      const unassigned = !usersBuildingsRow.buildingName;

      if (tableColumn === 'buildingName') {
        const buildingName = (usersBuildingsRow.buildingName || '')
          .toLowerCase()
          .replace(/\d+/g, (m) => m.padStart(10, '0'));
        return `${!unassigned}|${buildingName}`;
      }
      if (tableColumn === 'emailAddress') {
        return `${!unassigned}|${(usersBuildingsRow.emailAddress || '').toLowerCase()}`;
      }
      return `${!unassigned}|${String((usersBuildingsRow as any)[tableColumn] ?? '').toLowerCase()}`;
    };

    this.didInit = true;
    this.applyInitialSort();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.buildingUsers) {
      const flattened: UserWithBuilding[] = [];
      for (const buildingUsersDto of this.buildingUsers) {
        const buildingId = buildingUsersDto.building?.id ?? undefined;
        const buildingName = buildingUsersDto.building?.name ?? '';
        for (const user of buildingUsersDto.users || []) {
          flattened.push({ ...user, buildingId, buildingName });
        }
      }
      this.dataSource.data = flattened;

      (this.dataSource as any)._updateChangeSubscription?.();

      if (this.didInit) {
        this.applyInitialSort();
      }
      this.changeDetectorRef.markForCheck();
    }
  }

  private applyInitialSort(): void {
    if (!this.sort) return;

    queueMicrotask(() => {
      this.sort.active = 'buildingName';
      this.sort.direction = 'asc';
      this.sort.sortChange.emit({ active: 'buildingName', direction: 'asc' } as Sort);
      this.dataSource.sort = this.sort;
    });
  }

  onDelete(userWithBuilding: UserWithBuilding) {
    this.deleteRequested.emit({ buildingId: userWithBuilding.buildingId, userId: userWithBuilding.id });
  }

  formatLastLoggedIn(utcDate: Date): string {
    return TimeUtils.formatUtcToLocalCompact(new Date(utcDate));
  }

  checkIfMCAdminAndNotInBuilding(user: UserWithBuilding): boolean {
    if (user.buildingId != null || this.managingCompanyId == null) {
      return false;
    }
    return (user.managingCompanyAuthorities ?? []).some(
      (mca) =>
        mca.managingCompanyId === this.managingCompanyId &&
        (mca.authorities ?? []).some((authority) => authority === ManagingCompanyAuthorityType.MANAGING_COMPANY_ADMIN)
    );
  }

  onEdit(userWithBuilding: UserWithBuilding): void {
    this.editRequested.emit(userWithBuilding);
  }
}
